import { createClient } from '@supabase/supabase-js';

// Load from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Comprehensive environment variable validation
const validateEnvironment = () => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check Supabase configuration
  if (!supabaseUrl) {
    errors.push('REACT_APP_SUPABASE_URL is missing');
  } else if (!supabaseUrl.startsWith('https://')) {
    errors.push('REACT_APP_SUPABASE_URL must start with https://');
  }

  if (!supabaseKey) {
    errors.push('REACT_APP_SUPABASE_ANON_KEY is missing');
  } else if (supabaseKey.length < 100) {
    warnings.push('REACT_APP_SUPABASE_ANON_KEY seems too short - check if it\'s correct');
  }

  // The OpenAI key is deliberately not read here. Any process.env reference in
  // src/ is inlined into the public bundle by CRA, so the key stays server-side
  // in /api/openai-chat only.

  // Report issues
  if (errors.length > 0) {
    console.error('❌ ENVIRONMENT CONFIGURATION ERRORS:');
    errors.forEach(error => console.error(`  - ${error}`));
    console.error('\n📝 Setup Instructions:');
    console.error('  1. Copy env.example to .env.local');
    console.error('  2. Fill in your API credentials');
    console.error('  3. Restart dev server: npm start');
    console.error('  4. Check https://docs.clubiq.golf/setup for detailed instructions');
    // Don't throw error - allow app to continue for development
    console.warn('⚠️ App may not function correctly without proper configuration');
  }

  if (warnings.length > 0) {
    console.warn('⚠️ ENVIRONMENT CONFIGURATION WARNINGS:');
    warnings.forEach(warning => console.warn(`  - ${warning}`));
    console.warn('💡 These warnings won\'t prevent startup but may cause issues');
  }

  console.log('✅ Environment configuration validated');
  return true;
};

// Validate on module load
validateEnvironment();

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Database types
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          first_name: string;
          last_name: string;
          email: string;
          handicap: number | null;
          height: number;
          weight: number;
          birthdate: string | null;
          age: number;
          average_score: number | null;
          golf_persona: string | null;
          swing_speed: number;
          swing_speed_range: 'under-85' | '85-95' | '95-105' | 'over-105' | null;
          swing_tempo: 'slow' | 'medium' | 'fast';
          ball_flight: 'fade' | 'draw' | 'straight' | 'slice' | 'hook';
          hand_size: 'small' | 'medium' | 'large' | null;
          grip_type: 'interlock' | 'overlap' | 'baseball' | 'ten-finger' | null;
          grip_pressure: 'light' | 'medium' | 'firm' | null;
          attack_angle: 'steep' | 'neutral' | 'shallow' | null;
          skill_level: 'beginner' | 'intermediate' | 'advanced';
          playing_experience: 'beginner' | 'casual' | 'regular' | 'serious';
          course_frequency: 'rarely' | 'monthly' | 'weekly' | 'multiple';
          miss_pattern: 'left' | 'right' | 'short' | 'long' | 'inconsistent' | null;
          preferred_course_type: 'links' | 'parkland' | 'resort' | 'municipal' | 'private' | null;
          current_issues: string[];
          golf_goals: string[];
          club_distances: any;
          golf_bag: any;
          preferred_units: 'yards' | 'meters';
          has_completed_onboarding: boolean;
          onboarding_skipped_at: string | null;
          referral_code: string | null;
          referred_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          first_name: string;
          last_name: string;
          email: string;
          handicap?: number | null;
          height: number;
          weight: number;
          birthdate?: string | null;
          age: number;
          average_score?: number | null;
          golf_persona?: string | null;
          swing_speed: number;
          swing_speed_range?: 'under-85' | '85-95' | '95-105' | 'over-105' | null;
          swing_tempo: 'slow' | 'medium' | 'fast';
          ball_flight: 'fade' | 'draw' | 'straight' | 'slice' | 'hook';
          hand_size?: 'small' | 'medium' | 'large' | null;
          grip_type?: 'interlock' | 'overlap' | 'baseball' | 'ten-finger' | null;
          grip_pressure?: 'light' | 'medium' | 'firm' | null;
          attack_angle?: 'steep' | 'neutral' | 'shallow' | null;
          skill_level: 'beginner' | 'intermediate' | 'advanced';
          playing_experience?: 'beginner' | 'casual' | 'regular' | 'serious';
          course_frequency?: 'rarely' | 'monthly' | 'weekly' | 'multiple';
          miss_pattern?: 'left' | 'right' | 'short' | 'long' | 'inconsistent' | null;
          preferred_course_type?: 'links' | 'parkland' | 'resort' | 'municipal' | 'private' | null;
          current_issues?: string[];
          golf_goals?: string[];
          club_distances?: any;
          golf_bag?: any;
          preferred_units?: 'yards' | 'meters';
          has_completed_onboarding?: boolean;
          onboarding_skipped_at?: string | null;
          referral_code?: string | null;
          referred_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          first_name?: string;
          last_name?: string;
          email?: string;
          handicap?: number | null;
          height?: number;
          weight?: number;
          birthdate?: string | null;
          age?: number;
          average_score?: number | null;
          golf_persona?: string | null;
          swing_speed?: number;
          swing_speed_range?: 'under-85' | '85-95' | '95-105' | 'over-105' | null;
          swing_tempo?: 'slow' | 'medium' | 'fast';
          ball_flight?: 'fade' | 'draw' | 'straight' | 'slice' | 'hook';
          hand_size?: 'small' | 'medium' | 'large' | null;
          grip_type?: 'interlock' | 'overlap' | 'baseball' | 'ten-finger' | null;
          grip_pressure?: 'light' | 'medium' | 'firm' | null;
          attack_angle?: 'steep' | 'neutral' | 'shallow' | null;
          skill_level?: 'beginner' | 'intermediate' | 'advanced';
          playing_experience?: 'beginner' | 'casual' | 'regular' | 'serious';
          course_frequency?: 'rarely' | 'monthly' | 'weekly' | 'multiple';
          miss_pattern?: 'left' | 'right' | 'short' | 'long' | 'inconsistent' | null;
          preferred_course_type?: 'links' | 'parkland' | 'resort' | 'municipal' | 'private' | null;
          current_issues?: string[];
          golf_goals?: string[];
          club_distances?: any;
          golf_bag?: any;
          preferred_units?: 'yards' | 'meters';
          has_completed_onboarding?: boolean;
          onboarding_skipped_at?: string | null;
          referral_code?: string | null;
          referred_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      referrals: {
        Row: {
          id: string;
          referrer_id: string;
          referred_id: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          referrer_id: string;
          referred_id: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          referrer_id?: string;
          referred_id?: string;
          created_at?: string;
        };
      };
    };
  };
}
